import { stripHtml } from '../helpers/stringHelpers';
import { ChatProgress } from '../types/signlaRTypes';
import { getRenderableStreamingHtml } from './htmlSnapshots';

export const MAX_CHAT_PROGRESS_HISTORY_ENTRIES = 50;

export const getChatProgressDuration = (elapsedMs?: number) => {
  if (elapsedMs === undefined || !Number.isFinite(elapsedMs) || elapsedMs < 0) return undefined;
  const totalSeconds = Math.floor(elapsedMs / 1000);
  return { minutes: Math.floor(totalSeconds / 60), seconds: totalSeconds % 60 };
};

export const chatProgressLabels: Record<Exclude<ChatProgress['stage'], 'finished' | 'source_accepted'>, string> = {
  working: 'Working on your request...',
  searching: 'Searching sources...',
  searching_documents: 'Searching documents...',
  searching_web: 'Searching the web...',
  searching_documents_web: 'Searching documents and the web...',
  reviewing_sources: 'Reviewing sources...',
  reading_documents: 'Reading relevant documents...',
  reading_web_sources: 'Reading web pages...',
  checking_source_safety: 'Checking source content...',
  assessing_sources: 'Assessing relevant information...',
  combining_sources: 'Bringing the sources together...',
  summarizing_document: 'Summarizing the document...',
  summarizing_discussion: 'Summarizing the discussion...',
  reviewing_health: 'Reviewing health information...',
  updating_symptoms: 'Updating symptom information...',
  updating_medications: 'Updating medication information...',
  updating_treatments: 'Updating treatment information...',
  updating_diagnoses: 'Updating diagnosis information...',
  updating_journal: 'Updating the journal...',
  updating_health: 'Updating health information...',
  preparing_email: 'Preparing the email...',
  checking_members: 'Checking the member list...',
  creating_community: 'Setting up the community...',
  updating_community: 'Updating community settings...',
  removing_community: 'Processing the community removal...',
  updating_members: 'Updating community membership...',
  updating_permissions: 'Updating member permissions...',
  checking_notifications: 'Checking notifications...',
  checking_profile: 'Checking profile details...',
  updating_profile: 'Updating profile details...',
  updating_credentials: 'Updating background details...',
  updating_journey: 'Updating the health journey...',
  updating_preferences: 'Updating preferences...',
  checking_onboarding: 'Checking setup progress...',
  updating_onboarding: 'Updating setup progress...',
  preparing_survey: 'Working on the survey...',
  recording_survey: 'Processing the survey answer...',
  reviewing_needs: 'Reviewing support needs...',
  updating_needs: 'Updating support needs...',
  finding_sites: 'Checking available websites...',
  preparing_response: 'Getting the response ready...',
  tool_running: 'Using a tool...',
  tool_returned: 'Processing the result...',
  composing: 'Preparing the answer...',
  validating: 'Checking source references...',
  retrying: 'Trying the answer again...',
};

export const normalizeChatProgress = (raw: unknown): ChatProgress | undefined => {
  if (typeof raw === 'string') {
    if (raw.length > 4096) return undefined;
    try { raw = JSON.parse(raw); } catch { return undefined; }
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const progress = raw as ChatProgress;
  if (progress.version !== 1 || !Number.isSafeInteger(progress.sequence) || progress.sequence < 1) return undefined;
  if (typeof progress.stage !== 'string' || (progress.stage !== 'finished' && progress.stage !== 'source_accepted'
    && !Object.prototype.hasOwnProperty.call(chatProgressLabels, progress.stage))) return undefined;
  if (Object.prototype.hasOwnProperty.call(progress, 'sourceAccepted')) return undefined;
  if (progress.sourceKey !== undefined
    && (typeof progress.sourceKey !== 'string' || !/^[0-9a-f]{16}$/.test(progress.sourceKey))) return undefined;
  if (progress.stage === 'source_accepted' && progress.sourceKey === undefined) return undefined;
  if (![progress.responseId, progress.sessionId, progress.agentId, progress.legacyTarget]
    .every(value => typeof value === 'string' && value.trim().length > 0 && value.length <= 256)) return undefined;
  if (progress.sourceHost !== undefined && !isSourceHost(progress.sourceHost)) return undefined;
  if (progress.sourceTitle !== undefined && (typeof progress.sourceTitle !== 'string'
    || !progress.sourceTitle.trim() || [...progress.sourceTitle].length > 256
    || /[\u0000-\u001f\u007f-\u009f]/.test(progress.sourceTitle)
    || /[\uD800-\uDFFF]/u.test(progress.sourceTitle))) return undefined;
  if (progress.citationsFound !== undefined || progress.citationsRetained !== undefined) {
    if (!Number.isSafeInteger(progress.citationsFound) || !Number.isSafeInteger(progress.citationsRetained)
      || progress.citationsFound! < 0 || progress.citationsRetained! < 0
      || progress.citationsRetained! > progress.citationsFound!) return undefined;
  }
  return progress;
};

const isSourceHost = (value: unknown): value is string =>
  typeof value === 'string' && value.length <= 253 && value.includes('.') && !/^[\d.]+$/.test(value)
  && value.split('.').every(label => /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(label));

export const getChatProgressSourceLabel = (
  { stage, sourceTitle, sourceHost }: Pick<ChatProgress, 'stage' | 'sourceTitle' | 'sourceHost'>
): string | undefined => {
  switch (stage) {
    case 'reading_documents':
    case 'summarizing_document': return sourceTitle;
    case 'reading_web_sources': return sourceHost;
    case 'checking_source_safety':
    case 'assessing_sources': return sourceTitle ?? sourceHost;
    default: return undefined;
  }
};

export const getLatestChatProgressCitations = (history?: readonly ChatProgress[]) => {
  for (let index = (history?.length ?? 0) - 1; index >= 0; index--) {
    const entry = history![index];
    if (entry.citationsFound !== undefined && entry.citationsRetained !== undefined) {
      return { found: entry.citationsFound, retained: entry.citationsRetained };
    }
  }
  return undefined;
};

export const hasVisibleAIContent = (html: string): boolean =>
  stripHtml(getRenderableStreamingHtml(html)).replace(/&nbsp;|&#160;|&#xA0;/gi, ' ').trim().length > 0;